import { gql } from '@apollo/client';
import EditIcon from '@material-ui/icons/Edit';
import { SortHeader } from 'common';
import { usePopupTrigger$ } from 'common/hook';
import { Heading, Pane, Spinner, Table } from 'evergreen-ui';
import { useLazyQuery$ } from 'graphqlsupport';
import { Direction, Sort } from 'pagination';
import React, { memo, Reducer, useEffect, useReducer, useRef, useState } from 'react';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { useObservable } from 'rxsupport';
import { ClickableColumnHeader } from '../../common/component/ClickableColumnHeader';
import {
    BaseProviderUserInfo,
    CursorBasedPage,
    SortableProviderUserField,
    UserManagementColumns
} from '../domain/types';
import { EditUserPopup } from './EditUserPopup';
import { NewUserPopup } from './NewUserPopup';
import { UserFilterInput } from './ProviderUserFilterInput';
import { TableRowCell } from './TableRowCell';
import { ProviderUserImportPopup } from "usermanagementapp/user-import/ProviderUserImportPopup";
import { UserImportHistoryPopup } from 'usermanagementapp/user-import/UserImportHistoryPopup';
import { ApplicationRootUrl, useApplicationRoot } from 'common/component/ApplicationRootContext';
import {showToast} from "common";

type ProviderUserQueryInput = {
    after: string | null,
    filter: string,
    sortBy: keyof SortableProviderUserField,
    sortDir: Direction,
    isViewAsProvider: boolean,
}
type ProviderUserQueryResult = {
    providerUsers: CursorBasedPage<BaseProviderUserInfo>;
}
export const PROVIDER_USER_QUERY = gql`
query providerUsers($filter: String = "",$first: Int = 20, $sortBy: String, $sortDir: String, $after: String, $isViewAsProvider: Boolean) {
  providerUsers(
    params: {
      filter: $filter  
      first: $first
      sortBy: $sortBy
      sortDir: $sortDir
      after: $after
      isViewAsProvider: $isViewAsProvider
    }
  ) {
    nextPageCursor
    items {
      oktaId
      username
      email
      firstName
      lastName
      status
      providerName
      userManagementAdminAccess
    }
  }
}
`;


type ProviderUserListState = {
    users: BaseProviderUserInfo[];
    nextPageCursor: string | null;
};
type ProviderUserListAction = {
    type: 'INIT_RESULT' | 'MORE_RESULT',
    payload: CursorBasedPage<BaseProviderUserInfo>;
};
const providerUserListReducer: Reducer<ProviderUserListState, ProviderUserListAction> = (prevState, action) => {
    switch (action.type) {
        case 'INIT_RESULT':
            return {
                nextPageCursor: action.payload.nextPageCursor,
                users: [
                    ...action.payload.items,
                ],
            };
        case 'MORE_RESULT':
            return {
                nextPageCursor: action.payload.nextPageCursor,
                users: [
                    ...prevState.users,
                    ...action.payload.items,
                ],
            };
        default:
            console.error(`Unsupported action type ${action.type}`);
            return prevState;
    }
};

type ProviderUserQueryHookResult = {
    isRunning$: Observable<boolean>,
    currentFilter$: BehaviorSubject<string>,
    currentSort$: BehaviorSubject<Sort<SortableProviderUserField>>,
    error: string | null,
    users: BaseProviderUserInfo[],
    fetchMore: (() => Promise<void>) | null,
    refresh: (() => Promise<void>),
}
const useProviderUserQuery = (defaultSort: keyof SortableProviderUserField = 'providerName'): ProviderUserQueryHookResult => {
    const [state, dispatch] = useReducer(providerUserListReducer, {
        users: [],
        nextPageCursor: null,
    });

    const [currentFilter$] = useState(new BehaviorSubject<string>(''));
    const [currentSort$] = useState(new BehaviorSubject<Sort<SortableProviderUserField>>({
        direction: Direction.ASC,
        by: defaultSort,
    }));

    const applicationRoot = useApplicationRoot();
    const isViewAsProvider = applicationRoot === ApplicationRootUrl.AZURE_INTERNAL_UM_PROVIDER_ROUTE_NAME;


    const {
        isRunning$,
        data,
        variables,
        error,
        execute,
    } = useLazyQuery$<ProviderUserQueryInput, ProviderUserQueryResult>(PROVIDER_USER_QUERY, {
        isViewAsProvider,
        after: null,
        filter: currentFilter$.getValue(),
        sortBy: currentSort$.getValue().by,
        sortDir: currentSort$.getValue().direction,
    },
    );
    useEffect(() => {
        const autoFetchUponSortChange = combineLatest([currentFilter$, currentSort$])
            .subscribe(([filter, sort]) => execute({
                isViewAsProvider,
                after: null,
                filter,
                ...sort,
            }));
        return () => autoFetchUponSortChange.unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFilter$, currentSort$]);
    useEffect(() => {
        let unmounted = false;
        if (!unmounted && data?.providerUsers) {
            dispatch({
                type: variables?.after ? 'MORE_RESULT' : 'INIT_RESULT',
                payload: data?.providerUsers,
            })
        }
        return () => {
            unmounted = true;
        }
    }, [data, variables]);


    const fetchMore = state.nextPageCursor ? async () => {
        await execute({ after: state.nextPageCursor });
    } : null;

    const refresh = () => execute({ after: null, filter: currentFilter$.getValue(), ...currentSort$.getValue(), isViewAsProvider });

    return {
        isRunning$,
        currentFilter$,
        currentSort$,
        error: error?.message ?? null,
        users: state.users,
        fetchMore,
        refresh,
    };
};

type ProviderUserListProps = {
    fieldsToRender: UserManagementColumns;
    tableBodyHeight: string
};
export const ProviderUserList = memo(({ fieldsToRender, tableBodyHeight }: ProviderUserListProps) => {
    const { isRunning$, currentFilter$, currentSort$, users, error, fetchMore, refresh } = useProviderUserQuery(fieldsToRender[0].name);
    const loading = useObservable(isRunning$, true);
    if (error) {
        showToast.error(`Unable to fetch Provider users. Reason: ${error}`);
    }
    const [currentSelect, setCurrentSelect] = React.useState('');

    const { show, visibility$, hide } = usePopupTrigger$();
    const selectedUserIdRef = useRef<string | null>(null);

    return (
        <Pane padding='16px'>
            <Pane display='flex' justifyContent='space-between'>
                <UserFilterInput currentFilter$={currentFilter$} isRunning$={isRunning$} />
                <div className="flex content-between">
                    <NewUserPopup onSuccess={refresh} />
                    <div className="p-2"/>
                    <ProviderUserImportPopup />
                    <div className="p-2"/>
                    <UserImportHistoryPopup />
                </div>
            </Pane>
            <Heading size={600} marginBottom='24px'>Providers</Heading>
            <Table>
                <Table.Head height={40}
                    className='bg-white ev-overwrite ev-user-table-none-border user-table-pl-6-first-child um-thead'
                    paddingRight={0}>
                    {fieldsToRender.map((field) => {
                        return !!field.isUnsortable ? (
                            <ClickableColumnHeader key={field.name as string}
                                text={field.label}
                                loading={true}
                                className='ev-overwrite ev-user-table-header ev-user-table-header-pl'
                                buttonClassName="ev-overwrite um-table-header-btn"
                                {...(field.maxWidth ? { maxWidth: field.maxWidth } : {})}
                            />
                        ) : (
                                <SortHeader key={field.name as string}
                                    columnDefinition={field}
                                    isRunning$={isRunning$}
                                    currentSort$={currentSort$}
                                    className='ev-overwrite ev-user-table-header ev-user-table-header-pl'
                                    buttonClassName="ev-overwrite um-table-header-btn"
                                />
                            );
                    })}
                </Table.Head>
                <Table.VirtualBody height={tableBodyHeight} className='ev-overwrite scrollbar-always'>
                    <>
                        {
                            users.map(
                                user => (
                                    <Table.Row
                                        className='ev-overwrite ev-user-table-none-border relative user-table-pl-6-first-child'
                                        key={user.oktaId}
                                        onSelect={() => setCurrentSelect(user.oktaId)}
                                        isSelectable>
                                        {fieldsToRender.map(field =>
                                            <TableRowCell key={field.name}
                                                {...(field.maxWidth ? { maxWidth: field.maxWidth } : {})}
                                            >
                                                {(field.render && field.render(user[field.name])) || user[field.name]}
                                            </TableRowCell>)}
                                        {currentSelect === user.oktaId &&
                                            <div className='floating-user-edit-position z-20'>
                                                <EditUserButton onClick={() => {
                                                    selectedUserIdRef.current = user.oktaId;
                                                    show();
                                                }} />
                                            </div>
                                        }
                                        <Pane className="ev-overwrite scrollbar-padding" />
                                    </Table.Row>
                                ))
                        }
                        {loading &&
                            <Table.Row className='ev-overwrite ev-user-table-none-border'><Table.Cell><Spinner data-testid={"um-provider-user-list-loading"} size={24}
                                marginX='auto' /></Table.Cell></Table.Row>}
                        {(fetchMore) && (
                            <button
                                className='bg-white h-10 w-32 my-4 py-2 px-1 text-atlantis-200 border
                                        hover:bg-gray-200 border-atlantis-200 font-semibold disabled:opacity-50
                                        text-sm focus:outline-none focus:ring focus:border-atlantis-300 rounded'
                                onClick={fetchMore}>Load More
                            </button>
                        )}
                        {
                            !loading && users.length === 0 && <div className="text-center">No users available</div>
                        }
                    </>
                </Table.VirtualBody>
            </Table>
            <EditUserPopup selectedUserIdRef={selectedUserIdRef} visibility$={visibility$} hide={hide} onSuccess={refresh} />
        </Pane>
    );
});

type EditUserButtonProps = {
    onClick: () => void;
};
const EditUserButton = ({ onClick }: EditUserButtonProps) => {
    return (
        <button className="border-atlantis-200 hover:bg-gray-200 bg-transparent text-atlantis-200 outline-none
                                                    focus:outline-none h-8 w-8 text-center font-extrabold flex items-center justify-center text-xl
                                                    rounded-full border"
            onClick={onClick}>
            <EditIcon fontSize='inherit' />
        </button>
    );
}
